import Navbar from '../components/Navbar';
import { colors, typography, components, presets, cn, Footer } from '../components/Constants';

export default function PrivacyPolicy() {
  return (
    <>
      <Navbar />
      <div className={presets.pageWrapper}>
        <div className={presets.mainCard}>
          <h1 className={cn(typography.h1, colors.text.gradient)}>Privacy Policy</h1>
          <p className={typography.small}>Last Updated: October 27, 2025</p>
          
          <p className={typography.bodyMb}>
            KaydSystems ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our managed DevOps services.
          </p>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>1. Information We Collect</h2>
            
            <h3 className={presets.subsectionTitle}>1.1 Information You Provide</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Account Information:</strong> Name, email address, company name, phone number, billing address</li>
              <li><strong className={typography.strong}>Payment Information:</strong> Credit card details, billing information (processed through secure third-party payment processors)</li>
              <li><strong className={typography.strong}>Technical Information:</strong> Source code, application configurations, API keys, credentials, infrastructure specifications</li>
              <li><strong className={typography.strong}>Communications:</strong> Support tickets, emails, chat messages, phone calls</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>1.2 Information We Collect Automatically</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li><strong className={typography.strong}>Usage Data:</strong> Application performance metrics, system logs, resource utilization</li>
              <li><strong className={typography.strong}>Infrastructure Data:</strong> Server configurations, deployment history, error logs</li>
              <li><strong className={typography.strong}>Website Data:</strong> IP addresses, browser type, pages visited, time spent on pages</li>
              <li><strong className={typography.strong}>Monitoring Data:</strong> System health metrics, uptime statistics, security events</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>1.3 Information from Third Parties</h3>
            <ul className={presets.listItem}>
              <li>Cloud provider logs and metrics (AWS, GCP, Azure)</li>
              <li>Payment processor information</li>
              <li>Authentication provider data (if applicable)</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>2. How We Use Your Information</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>We use collected information to:</p>
            <ul className={presets.listItem}>
              <li><strong className={typography.strong}>Provide Services:</strong> Deploy, host, monitor, and maintain your applications</li>
              <li><strong className={typography.strong}>Support:</strong> Respond to inquiries, troubleshoot issues, provide technical assistance</li>
              <li><strong className={typography.strong}>Security:</strong> Detect and prevent security threats, unauthorized access, and fraudulent activity</li>
              <li><strong className={typography.strong}>Improvements:</strong> Analyze usage patterns, optimize performance, develop new features</li>
              <li><strong className={typography.strong}>Billing:</strong> Process payments, send invoices, manage subscriptions</li>
              <li><strong className={typography.strong}>Communications:</strong> Send service updates, security alerts, maintenance notices</li>
              <li><strong className={typography.strong}>Compliance:</strong> Meet legal obligations, respond to legal requests</li>
              <li><strong className={typography.strong}>Business Operations:</strong> Internal analytics, quality assurance, training</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>3. How We Share Your Information</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>We do not sell your personal information. We may share information with:</p>
            
            <h3 className={presets.subsectionTitle}>3.1 Service Providers</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Cloud infrastructure providers (AWS, Google Cloud, Azure)</li>
              <li>Payment processors</li>
              <li>Monitoring and analytics tools</li>
              <li>Security service providers</li>
              <li>Email and communication services</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>3.2 Legal Requirements</h3>
            <p className={cn(presets.bodyText, 'mb-3')}>We may disclose information if required by law or in response to:</p>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Valid legal processes (subpoenas, court orders)</li>
              <li>Government requests</li>
              <li>Protection of rights, property, or safety</li>
              <li>Investigation of fraud or security issues</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>3.3 Business Transfers</h3>
            <p className={presets.bodyText}>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>4. Data Security</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>We implement robust security measures including:</p>
            <ul className={presets.listItem}>
              <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Role-based access controls</li>
              <li>Multi-factor authentication</li>
              <li>Regular security audits and penetration testing</li>
              <li>Network segmentation and firewall protection</li>
              <li>Intrusion detection and prevention systems</li>
              <li>Regular security updates and patch management</li>
              <li>Employee security training and background checks</li>
              <li>Incident response procedures</li>
            </ul>
            
            <div className={cn(components.alertBox('red'), 'mt-4')}>
              <p className={cn('font-semibold', colors.text.primary, 'mb-2')}>Security Incident Notification:</p>
              <p className={presets.bodyText}>
                In the event of a data breach affecting your personal information, we will notify you within 72 hours of discovery, as required by applicable law.
              </p>
            </div>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>5. Data Retention</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>
              We retain your information for as long as necessary to provide services and comply with legal obligations:
            </p>
            <ul className={presets.listItem}>
              <li><strong className={typography.strong}>Account Information:</strong> Duration of service plus 7 years for financial records</li>
              <li><strong className={typography.strong}>Technical Data:</strong> 90 days for logs, longer for compliance requirements</li>
              <li><strong className={typography.strong}>Backups:</strong> 30 days after account termination</li>
              <li><strong className={typography.strong}>Legal Records:</strong> As required by applicable law</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>6. Your Rights and Choices</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>Depending on your location, you may have the following rights:</p>
            
            <h3 className={presets.subsectionTitle}>6.1 Access and Portability</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Request a copy of your personal information</li>
              <li>Export your application data and infrastructure configurations</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.2 Correction</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Update inaccurate or incomplete information</li>
              <li>Request correction of erroneous data</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.3 Deletion</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Request deletion of your personal information</li>
              <li>Note: Some data may be retained for legal or operational purposes</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.4 Objection and Restriction</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Object to certain data processing activities</li>
              <li>Request restriction of processing under specific circumstances</li>
            </ul>
            
            <h3 className={presets.subsectionTitle}>6.5 Withdrawal of Consent</h3>
            <ul className={cn(presets.listItem, 'mb-4')}>
              <li>Withdraw consent for data processing where consent was the legal basis</li>
              <li>Note: Withdrawal may limit our ability to provide services</li>
            </ul>
            
            <p className={cn(presets.bodyText, 'mt-4')}>To exercise these rights, contact us at info@kaydsystems.com</p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>7. International Data Transfers</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>
              Your information may be processed in countries other than your own. We ensure adequate protections through:
            </p>
            <ul className={presets.listItem}>
              <li>Standard Contractual Clauses (SCCs) for EU data transfers</li>
              <li>Compliance with applicable data protection frameworks</li>
              <li>Security measures equivalent to those in your jurisdiction</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>8. Cookies and Tracking Technologies</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>Our website uses cookies and similar technologies:</p>
            <ul className={presets.listItem}>
              <li><strong className={typography.strong}>Essential Cookies:</strong> Required for website functionality</li>
              <li><strong className={typography.strong}>Analytics Cookies:</strong> Help us understand website usage</li>
              <li><strong className={typography.strong}>Security Cookies:</strong> Detect fraudulent activity and protect accounts</li>
            </ul>
            <p className={cn(presets.bodyText, 'mt-3')}>You can control cookies through your browser settings.</p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>9. Children's Privacy</h2>
            <p className={presets.bodyText}>
              Our services are not intended for individuals under 18. We do not knowingly collect information from children. If we learn we have collected such information, we will delete it promptly.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>10. Third-Party Links</h2>
            <p className={presets.bodyText}>
              Our website may contain links to third-party sites. We are not responsible for their privacy practices. We encourage you to review their privacy policies.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>11. California Privacy Rights (CCPA)</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>California residents have additional rights under the California Consumer Privacy Act:</p>
            <ul className={presets.listItem}>
              <li>Know what personal information is collected, used, shared, or sold</li>
              <li>Delete personal information (subject to exceptions)</li>
              <li>Opt-out of sale of personal information (we do not sell information)</li>
              <li>Non-discrimination for exercising privacy rights</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>12. European Privacy Rights (GDPR)</h2>
            <p className={cn(presets.bodyText, 'mb-3')}>
              If you are in the European Economic Area (EEA), you have additional rights under GDPR:
            </p>
            <ul className={presets.listItem}>
              <li>Legal bases for processing: Contract performance, legitimate interests, legal obligations, consent</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
              <li>Data Protection Officer contact: dpo@kaydsystems.com</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>13. Changes to This Privacy Policy</h2>
            <p className={presets.bodyText}>
              We may update this Privacy Policy periodically. We will notify you of material changes via email or prominent notice on our website at least 30 days before changes take effect.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className={presets.sectionTitle}>14. Contact Us</h2>
            <div className={components.contactBox}>
              <h3 className={cn(typography.h4, colors.text.primary)}>Privacy Inquiries</h3>
              <p className={presets.bodyText}>Email: info@kaydsystems.com</p>
              <p className={presets.bodyText}>Address: 6701 Corporate Dr Ste N Johnston, IA 50131, United States</p>
              <p className={presets.bodyText}>Data Protection Officer: dpo@kaydsystems.com</p>
            </div>
          </section>
        </div>
        
        <Footer />
      </div>
    </>
  );
}